/**
 * MonitoringBoard — the COA monitoring scoreboard + one-time setup wizard.
 *
 * The user experience, in order:
 *   1. First visit: a short wizard — check the measures you monitor, pick a
 *      cadence, keep or change the 95% benchmark, pick who performs each
 *      (internal and/or FDRs). Five minutes, once.
 *   2. Every month after: rows appear by themselves and score by themselves
 *      from validated universes. Met (green), Missed (rose, with the reason),
 *      Awaiting data (slate), Overdue (amber).
 *   3. Manual measures and FDR entities take one number a month.
 *
 * Zero-PHI: everything on this board is a count or a percentage.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { fmtMeasure, unitOf } from './measureFormat';

const API = process.env.REACT_APP_API_URL || '';

const CADENCES = [
  { key: 'monthly', label: 'Monthly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'semiannual', label: 'Semi-annual' },
  { key: 'annual', label: 'Annual' },
];

const STATUS_CHIP = {
  met:      { label: 'Met',            cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  missed:   { label: 'Missed',         cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
  awaiting: { label: 'Awaiting data',  cls: 'bg-slate-100 text-slate-500 ring-slate-200' },
  overdue:  { label: 'Overdue',        cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
};

function StatusChip({ status }) {
  const c = STATUS_CHIP[status] || STATUS_CHIP.awaiting;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${c.cls}`}>
      {c.label}
    </span>
  );
}

/** Tiny inline sparkline from [{source_period, numerator, denominator}] oldest→newest. */
function Sparkline({ points, benchmark }) {
  const rates = points.map((p) => (p.denominator > 0 ? (p.numerator / p.denominator) * 100 : null)).filter((v) => v !== null);
  if (rates.length < 2) return null;
  // A percentage tops out at 100, but a benchmark in seconds does not: a two
  // minute standard is 120 and would otherwise be drawn above the chart.
  const w = 84; const h = 24; const min = Math.min(...rates, benchmark) - 2;
  const max = Math.max(100, benchmark || 0, ...rates);
  const x = (i) => (i / (rates.length - 1)) * (w - 4) + 2;
  const y = (v) => h - 3 - ((v - min) / (max - min || 1)) * (h - 6);
  const path = rates.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} className="flex-shrink-0" aria-hidden="true">
      <line x1="2" x2={w - 2} y1={y(benchmark)} y2={y(benchmark)} stroke="#f59e0b" strokeWidth="1" strokeDasharray="3 2" opacity="0.6" />
      <path d={path} fill="none" stroke="#64748b" strokeWidth="1.5" />
      <circle cx={x(rates.length - 1)} cy={y(rates[rates.length - 1])} r="2.2"
        fill={rates[rates.length - 1] >= benchmark ? '#059669' : '#e11d48'} />
    </svg>
  );
}

/**
 * Tripwires — plain-language reasons a scored number deserves a second look
 * before anyone relies on it. Deterministic, computed from counts the engine
 * stored at scoring time. Empty array = nothing unusual.
 */
function reviewReasons(row) {
  const f = row.monitoring_flags;
  if (!f || !['met', 'missed'].includes(row.monitoring_status)) return [];
  const reasons = [];
  if (Number(f.submissions) > 1) {
    reasons.push(`${f.submissions} approved files fed this period — make sure they don't overlap`);
  }
  if (Number(f.out_of_period) > 0 && Number(f.denominator) > 0
      && Number(f.out_of_period) >= 0.1 * Number(f.denominator)) {
    reasons.push(`${f.out_of_period} case(s) dated outside this period (all counted) — confirm the file is the right universe`);
  }
  if (Number(f.typical_denominator) > 0 && Number(f.denominator) > 1.6 * Number(f.typical_denominator)) {
    reasons.push(`${f.denominator} cases vs a typical ${f.typical_denominator} — the file may span extra months`);
  }
  return reasons;
}

/** Human period label from a period key: '2026-06' → 'June 2026', '2026-Q2' → 'Q2 2026'. */
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function periodLabel(key) {
  if (/^\d{4}-\d{2}$/.test(key)) { const [y, m] = key.split('-'); return `${MONTHS[Number(m) - 1]} ${y}`; }
  if (/^\d{4}-Q[1-4]$/.test(key)) { const [y, q] = key.split('-'); return `${q} ${y}`; }
  if (/^\d{4}-H[12]$/.test(key)) { const [y, hh] = key.split('-'); return `${hh === 'H1' ? 'First half' : 'Second half'} ${y}`; }
  return key;
}

// ── Setup wizard ─────────────────────────────────────────────────────────────

export function SetupWizard({ measures, fdrs, departments = [], onClose, onSaved }) {
  // One row of state per (measure, entity) the user turns on.
  const [selected, setSelected] = useState({}); // key -> {measure, entity, cadence, benchmark, months}
  const [planYear, setPlanYear] = useState(new Date().getFullYear());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Which protocol sections are expanded. Sections start collapsed so the
  // whole plan fits on one screen; counts on the header say what's inside.
  const [open, setOpen] = useState({});
  // Ad-hoc measures the user defines right here: any department, any metric.
  const [adhoc, setAdhoc] = useState([]); // [{id, label}]
  const [adhocDept, setAdhocDept] = useState('');
  const [adhocWhat, setAdhocWhat] = useState('');
  const adhocSeq = useRef(1);
  // Users per department, fetched when a department is first chosen — the
  // owner dropdown offers exactly the people registered to that department.
  const [deptUsers, setDeptUsers] = useState({});
  const loadDeptUsers = (deptId) => {
    if (!deptId || deptUsers[deptId]) return;
    fetch(`${API}/api/users/by-department/${deptId}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setDeptUsers((prev) => ({ ...prev, [deptId]: d.users || [] })))
      .catch(() => setDeptUsers((prev) => ({ ...prev, [deptId]: [] })));
  };
  const MONTH_CHIPS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const groups = useMemo(() => {
    const g = {};
    for (const m of measures) {
      if (m.key === 'manual') continue; // replaced by the ad-hoc builder below
      (g[m.group] = g[m.group] || []).push(m);
    }
    return g;
  }, [measures]);

  const selKey = (measure, entity) =>
    `${measure.adhocId ? 'adhoc:' + measure.adhocId : measure.key}|${entity ? entity.id : 'internal'}`;

  // Seed the department from the measure's keywords when a registered
  // department matches (e.g. "Utilization" -> Utilization Management).
  // The user confirms or changes it — saving requires one on every row.
  const guessDept = (measure) => {
    if (measure.department_id) return measure.department_id; // ad-hoc carries its own
    const kws = measure.dept_keywords || [];
    for (const d of departments) {
      const name = String(d.name || '').toLowerCase();
      // Short keywords ('um') must match a whole word, or they hit inside
      // unrelated names ('hUMan resources'); longer ones may match anywhere.
      const words = name.split(/[^a-z0-9]+/);
      if (kws.some((kw) => {
        const k = String(kw).toLowerCase();
        return k.length <= 3 ? words.includes(k) : name.includes(k);
      })) return d.id;
    }
    return '';
  };

  const toggle = (measure, entity) => {
    const k = selKey(measure, entity);
    setSelected((prev) => {
      const next = { ...prev };
      if (next[k]) delete next[k];
      else {
        const department_id = guessDept(measure);
        if (department_id) loadDeptUsers(department_id);
        next[k] = { measure, entity: entity || null, cadence: 'monthly', benchmark: 95, noBenchmark: false, months: [1,2,3,4,5,6,7,8,9,10,11,12], department_id, owner_user_id: '' };
      }
      return next;
    });
  };
  const update = (k, field, value) => setSelected((prev) => ({ ...prev, [k]: { ...prev[k], [field]: value } }));
  const toggleMonth = (k, m) => setSelected((prev) => {
    const cur = prev[k]?.months || [];
    const months = cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m].sort((a, b) => a - b);
    return { ...prev, [k]: { ...prev[k], months } };
  });

  const addAdhoc = () => {
    const what = adhocWhat.trim();
    const dept = departments.find((d) => String(d.id) === String(adhocDept));
    if (!what || !dept) return;
    const label = `${dept.name} — ${what}`.slice(0, 160);
    setAdhoc((prev) => [...prev, { id: adhocSeq.current++, label, department_id: dept.id, department_name: dept.name }]);
    setAdhocDept(''); setAdhocWhat('');
  };
  const removeAdhoc = (id) => {
    setAdhoc((prev) => prev.filter((a) => a.id !== id));
    setSelected((prev) => {
      const next = {};
      for (const [k, v] of Object.entries(prev)) {
        if (!k.startsWith(`adhoc:${id}|`)) next[k] = v;
      }
      return next;
    });
  };

  const save = async () => {
    const unassigned = Object.values(selected).filter((s) => !s.department_id);
    if (unassigned.length) {
      setError(`Assign a department to every selection — ${unassigned.length} still unassigned (marked in red).`);
      return;
    }
    setSaving(true); setError(null);
    const series = Object.values(selected).map((s) => ({
      measure_key: s.measure.adhocId ? 'manual' : s.measure.key,
      ...(s.measure.adhocId ? { measure_label: s.measure.label } : {}),
      department_id: s.department_id,
      ...(s.owner_user_id ? { owner_user_id: s.owner_user_id } : {}),
      cadence: s.cadence,
      benchmark: s.noBenchmark ? null : s.benchmark,
      months: s.months && s.months.length ? s.months : null,
      plan_year: planYear,
      entity_kind: s.entity ? 'fdr' : 'internal',
      fdr_entity_id: s.entity ? s.entity.id : null,
      entity_name: s.entity ? s.entity.name : 'Internal',
    }));
    try {
      const res = await fetch(`${API}/api/coa/monitoring/series`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ series }),
      });
      const data = await res.json();
      if (!data.success) { setError((data.errors || []).join('; ') || data.message || 'Could not save.'); setSaving(false); return; }
      // The plan is annual: every selected month's row exists from day one,
      // future periods as 'scheduled'. Idempotent server-side.
      try {
        const gen = await fetch(`${API}/api/coa/plan/generate`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan_year: planYear }),
        }).then((r) => r.json());
        if (gen && gen.success) data.rows_created = gen.rows_created;
      } catch (genErr) { /* rows can be generated again from the board */ }
      onSaved(data);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  const count = Object.keys(selected).length;
  const groupSelectedCount = (items) => {
    const keys = new Set(items.map((m) => m.key));
    return Object.values(selected).filter((s) => !s.measure.adhocId && keys.has(s.measure.key)).length;
  };
  const adhocSelectedCount = Object.values(selected).filter((s) => s.measure.adhocId).length;

  // What a card syncs against, stated on the card itself.
  const sourceBadge = (m) => {
    if (m.source === 'scrubber') return `Scores from validated universes — syncs against ${m.protocol} · ${m.table}`;
    if (m.source === 'reporter') return 'Scores automatically from the Reporter module';
    if (m.source === 'privacy') return 'Scores automatically from the Privacy module';
    if (m.source === 'cil') return 'Scores automatically from the Compliance Issues log';
    if (m.source === 'cap') return 'Scores automatically from Corrective Action Plans';
    return 'Reported monthly by email — the assignee enters the numbers, the tool scores them';
  };
  const deptOf = (m) => (m.component || '').split('—')[0].trim();

  // One measure card: name, what's measured, sync target, entity toggles.
  const MeasureCard = (m, onRemove) => {
    const entities = [null, ...fdrs];
    return (
      <div key={m.adhocId ? `adhoc-${m.adhocId}` : m.key} className="rounded-xl border border-slate-200/70 bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800">{m.label}</p>
            {m.function_desc && (
              <p className="text-xs text-slate-500 mt-0.5">Measures {m.function_desc}.</p>
            )}
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${m.source === 'scrubber' ? 'bg-blue-50 text-blue-700 border border-blue-200/70' : m.adhocId ? 'bg-amber-50 text-amber-700 border border-amber-200/70' : 'bg-emerald-50 text-emerald-700 border border-emerald-200/70'}`}>
                {sourceBadge(m)}
              </span>
              {deptOf(m) && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600">
                  {deptOf(m)}
                </span>
              )}
            </div>
          </div>
          {onRemove && (
            <button type="button" onClick={onRemove} className="text-slate-300 hover:text-rose-500 text-lg leading-none px-1" title="Remove this ad-hoc measure">×</button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {entities.map((entity) => {
            const k = selKey(m, entity);
            const on = !!selected[k];
            return (
              <div key={k} className={`rounded-lg border px-2.5 py-1.5 text-xs transition ${on ? 'border-orange-300 bg-orange-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={on} onChange={() => toggle(m, entity)}
                    className="rounded border-slate-300 text-orange-500 focus:ring-orange-400" />
                  <span className="text-slate-700">{entity ? entity.name : 'Internal'}</span>
                </label>
                {on && (
                  <div className="mt-1.5 space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-400">dept</span>
                      <select value={selected[k].department_id || ''}
                        onChange={(e) => { const v = e.target.value; loadDeptUsers(v); setSelected((prev) => ({ ...prev, [k]: { ...prev[k], department_id: v, owner_user_id: '' } })); }}
                        className={`rounded text-xs py-0.5 pr-6 max-w-[11rem] ${selected[k].department_id ? 'border-slate-200' : 'border-rose-300 bg-rose-50'}`}>
                        <option value="">— assign —</option>
                        {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                      {selected[k].department_id && (
                        <>
                          <span className="text-slate-400">owner</span>
                          <select value={selected[k].owner_user_id || ''} onChange={(e) => update(k, 'owner_user_id', e.target.value)}
                            className="rounded border-slate-200 text-xs py-0.5 pr-6 max-w-[11rem]">
                            <option value="">— department's users —</option>
                            {(deptUsers[selected[k].department_id] || []).map((u) => (
                              <option key={u.id} value={u.id}>{u.name || u.email}</option>
                            ))}
                          </select>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={selected[k].cadence} onChange={(e) => update(k, 'cadence', e.target.value)}
                        className="rounded border-slate-200 text-xs py-0.5 pr-6">
                        {CADENCES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                      <label className="flex items-center gap-1 text-slate-500 cursor-pointer">
                        <input type="checkbox" checked={!!selected[k].noBenchmark}
                          onChange={(e) => update(k, 'noBenchmark', e.target.checked)}
                          className="rounded border-slate-300 text-orange-500 focus:ring-orange-400" />
                        no benchmark
                      </label>
                      {!selected[k].noBenchmark && (
                        <>
                          <span className="text-slate-400">goal</span>
                          <input type="number" min="0" max="100" step="0.1" value={selected[k].benchmark}
                            onChange={(e) => update(k, 'benchmark', e.target.value)}
                            className="w-16 rounded border-slate-200 text-xs py-0.5" />
                          <span className="text-slate-400">%</span>
                        </>
                      )}
                    </div>
                    {selected[k].cadence === 'monthly' && (
                      <div className="flex flex-wrap gap-1">
                        {MONTH_CHIPS.map((mn, i) => {
                          const monthOn = (selected[k].months || []).includes(i + 1);
                          return (
                            <button key={mn} type="button" onClick={() => toggleMonth(k, i + 1)}
                              className={monthOn
                                ? 'px-1.5 py-0.5 rounded text-[10px] font-medium border transition border-orange-300 bg-orange-100 text-orange-800'
                                : 'px-1.5 py-0.5 rounded text-[10px] font-medium border transition border-slate-200 bg-white text-slate-400 hover:border-slate-300'}>
                              {mn}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Portal to <body>: the app shell animates (transforms) its content pane,
  // which re-anchors fixed-position children to the pane instead of the
  // viewport and slides the dialog off-screen at some window sizes.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-[min(72rem,95vw)] max-w-6xl max-h-[88vh] flex flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200/60 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-800">Build the annual work plan</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              Open a protocol, check each table to monitor, pick its months, internal or per-FDR, and the benchmark (or none). Every selected month gets its entry for the year, ready for CEO and Board approval.
            </p>
            <label className="inline-flex items-center gap-2 mt-2 text-sm text-slate-600">
              Plan year
              <input type="number" min="2020" max="2100" value={planYear}
                onChange={(e) => setPlanYear(Number(e.target.value))}
                className="w-24 rounded border-slate-200 text-sm py-1" />
            </label>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-2">×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {Object.entries(groups).map(([group, items]) => {
            const expanded = !!open[group];
            const sel = groupSelectedCount(items);
            return (
              <div key={group} className="rounded-xl border border-slate-200/70 overflow-hidden">
                <button type="button" onClick={() => setOpen((o) => ({ ...o, [group]: !o[group] }))}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-slate-50/60 hover:bg-slate-50 transition text-left">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`}>▸</span>
                    <span className="text-sm font-semibold text-slate-800 truncate">{group}</span>
                  </span>
                  <span className="flex items-center gap-2 flex-shrink-0">
                    {sel > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 text-orange-700">{sel} selected</span>
                    )}
                    <span className="text-xs text-slate-400">{items.length} {items.length === 1 ? 'measure' : 'measures'}</span>
                  </span>
                </button>
                {expanded && (
                  <div className="p-3 space-y-2 border-t border-slate-200/60">
                    {items.map((m) => MeasureCard(m))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Ad-hoc monitoring — any department, any metric. */}
          <div className="rounded-xl border border-amber-200/80 overflow-hidden">
            <div className="px-4 py-3 bg-amber-50/60">
              <p className="text-sm font-semibold text-slate-800">Ad-hoc monitoring — any department, any metric</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Name the department and what's being measured. The assignee gets a monthly email, enters the numbers, and the tool scores them against the benchmark — results land in Precision COA and Charting like everything else.
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <select value={adhocDept} onChange={(e) => setAdhocDept(e.target.value)}
                  className="w-52 rounded-lg border-slate-200 text-sm py-1.5">
                  <option value="">— Department —</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <input type="text" value={adhocWhat} onChange={(e) => setAdhocWhat(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAdhoc(); } }}
                  placeholder="What's measured (e.g. acknowledgement letters sent within 10 days)" maxLength={120}
                  className="flex-1 min-w-[16rem] rounded-lg border-slate-200 text-sm py-1.5" />
                <button type="button" onClick={addAdhoc} disabled={!adhocWhat.trim() || !adhocDept}
                  className="px-3.5 py-1.5 rounded-lg text-sm font-semibold bg-amber-500 text-white disabled:opacity-40 hover:bg-amber-600 transition">
                  Add
                </button>
              </div>
              {adhocSelectedCount > 0 && (
                <p className="text-[10px] font-semibold text-orange-700 mt-1.5">{adhocSelectedCount} selected</p>
              )}
            </div>
            {adhoc.length > 0 && (
              <div className="p-3 space-y-2 border-t border-amber-200/60">
                {adhoc.map((a) => MeasureCard(
                  { adhocId: a.id, key: 'manual', label: a.label, source: 'manual', component: a.department_name || '', function_desc: null, department_id: a.department_id },
                  () => removeAdhoc(a.id)
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200/60 flex items-center justify-between">
          {error ? <span className="text-sm text-rose-600">{error}</span> : (
            <span className="text-sm text-slate-500">{count} measure{count === 1 ? '' : 's'} selected</span>
          )}
          <button onClick={save} disabled={saving || count === 0}
            className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg text-sm font-semibold text-white disabled:opacity-50 hover:shadow-lg transition">
            {saving ? 'Saving…' : 'Start monitoring →'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Manual result entry ──────────────────────────────────────────────────────

function ManualEntry({ row, onSaved }) {
  // With a benchmark the person enters the result itself — 94 against 95%,
  // 1:27 against 2:00 (operator ruling 2026-08-28). Without one, the count.
  const hasBenchmark = row.benchmark !== null && row.benchmark !== undefined;
  const unit = row.benchmark_unit || 'percent';
  const [val, setVal] = useState('');
  const [num, setNum] = useState('');
  const [den, setDen] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${API}/api/coa/monitoring/activities/${row.id}/result`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(hasBenchmark ? { value: Number(val) } : { numerator: Number(num), denominator: Number(den) }),
      });
      const data = await res.json();
      if (!data.success) { setErr(data.message || 'Could not save'); setBusy(false); return; }
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  const ok = hasBenchmark && val !== '' && Number.isFinite(Number(val))
    ? ((row.direction === 'lower') ? Number(val) <= Number(row.benchmark) : Number(val) >= Number(row.benchmark))
    : null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {hasBenchmark ? (
        <>
          <input type="number" min="0" step="0.01" placeholder={unit === 'percent' ? 'Result %' : `Result (${unit})`} value={val} onChange={(e) => setVal(e.target.value)}
            className="w-28 rounded-lg border-slate-200 text-xs py-1" />
          <span className="text-slate-400 text-xs">vs {fmtMeasure(Number(row.benchmark), unit)}</span>
          {ok !== null && <span className={`text-xs font-semibold ${ok ? 'text-emerald-700' : 'text-rose-600'}`}>{ok ? 'meets' : 'misses'}</span>}
        </>
      ) : (
        <>
          <input type="number" min="0" placeholder="on time" value={num} onChange={(e) => setNum(e.target.value)}
            className="w-20 rounded-lg border-slate-200 text-xs py-1" />
          <span className="text-slate-400 text-xs">of</span>
          <input type="number" min="0" placeholder="total" value={den} onChange={(e) => setDen(e.target.value)}
            className="w-20 rounded-lg border-slate-200 text-xs py-1" />
        </>
      )}
      <button onClick={submit} disabled={busy || (hasBenchmark ? val === '' : (num === '' || den === ''))}
        className="px-2.5 py-1 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-xs font-medium disabled:opacity-40">
        {busy ? '…' : 'Save'}
      </button>
      {err && <span className="text-xs text-rose-600 ml-1">{err}</span>}
    </div>
  );
}

// ── The board ────────────────────────────────────────────────────────────────

export default function MonitoringBoard({ isCoaCompliance, onCreateCap }) {
  const [board, setBoard] = useState([]);
  const [trend, setTrend] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [fdrs, setFdrs] = useState([]);
  // Registered departments — every monitored area is assigned to one.
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/departments`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setDepartments(d.departments || d || []))
      .catch(() => setDepartments([]));
  }, []);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/coa/monitoring/board`, { credentials: 'include' }).then((r) => r.json()),
      fetch(`${API}/api/coa/monitoring/series`, { credentials: 'include' }).then((r) => r.json()),
      fetch(`${API}/api/coa/monitoring/measures`, { credentials: 'include' }).then((r) => r.json()),
      fetch(`${API}/api/fdr/entities`, { credentials: 'include' }).then((r) => r.json()).catch(() => ({})),
    ]).then(([b, s, m, f]) => {
      if (b.success) { setBoard(b.board || []); setTrend(b.trend || []); }
      if (s.success) setSeriesList(s.series || []);
      if (m.success) setMeasures(m.measures || []);
      setFdrs(f.entities || f.fdrs || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await fetch(`${API}/api/coa/monitoring/refresh`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    } finally { setRefreshing(false); load(); }
  };

  const byPeriod = useMemo(() => {
    const g = new Map();
    for (const row of board) {
      if (!g.has(row.source_period)) g.set(row.source_period, []);
      g.get(row.source_period).push(row);
    }
    return [...g.entries()];
  }, [board]);

  const trendFor = useCallback((seriesId) => {
    return trend.filter((t) => t.series_id === seriesId).slice(0, 12).reverse();
  }, [trend]);

  const activeSeries = seriesList.filter((s) => s.active).length;

  if (loading) {
    return <div className="py-16 text-center text-slate-500">Loading monitoring…</div>;
  }

  // First-run: nothing configured yet — the five-minute setup.
  if (activeSeries === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-12 text-center">
        <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center text-white text-2xl shadow-lg">📈</div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">Monitor timeliness without the spreadsheet</h3>
        <p className="text-slate-500 text-sm max-w-xl mx-auto mb-6">
          Pick the measures your plan watches — prior auth, appeals, grievances, Part D, and more — set a goal like 95%,
          and entries will create and score themselves each period from your validated universes. Met or missed, automatically,
          in CMS work plan format.
        </p>
        {isCoaCompliance ? (
          <button onClick={() => setWizardOpen(true)}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl text-sm font-semibold text-white hover:shadow-lg transition">
            Set up monitoring — about 5 minutes
          </button>
        ) : (
          <p className="text-sm text-slate-400">Your compliance team hasn't configured monitoring yet.</p>
        )}
        {wizardOpen && (
          <SetupWizard measures={measures} fdrs={fdrs} departments={departments} onClose={() => setWizardOpen(false)}
            onSaved={() => { setWizardOpen(false); refresh(); }} />
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Monitoring</h2>
          <p className="text-slate-500 text-sm">{activeSeries} measure{activeSeries === 1 ? '' : 's'} watched · entries create and score themselves each period</p>
        </div>
        {isCoaCompliance && (
          <div className="flex items-center gap-2">
            <button onClick={() => setWizardOpen(true)}
              className="px-3.5 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:border-slate-300 hover:text-slate-800 transition">
              + Add measures
            </button>
            <button onClick={refresh} disabled={refreshing}
              className="px-3.5 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium disabled:opacity-50 transition">
              {refreshing ? 'Checking…' : 'Check for new results'}
            </button>
          </div>
        )}
      </div>

      {byPeriod.map(([period, rows]) => (
        <div key={period} className="mb-6">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">{periodLabel(period)}</h3>
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm divide-y divide-slate-100">
            {rows.map((row) => {
              const rate = (() => {
                const ms = row.metrics || [];
                const den = ms.reduce((s, m) => s + (m.denominator || 0), 0);
                const num = ms.reduce((s, m) => s + (m.numerator || 0), 0);
                return den > 0 ? Math.round((num / den) * 1000) / 10 : null;
              })();
              // Any unscored period takes a result by hand — manual measures
              // always, synced measures only while their universe has not
              // arrived (operator ruling 2026-08-28). A universe replaces it.
              const needsManual = ['awaiting', 'overdue'].includes(row.monitoring_status);
              const isOpen = expanded === row.id;
              const reasons = reviewReasons(row);
              return (
                <div key={row.id} className="px-4 py-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <StatusChip status={row.monitoring_status} />
                    {reasons.length > 0 && (
                      <button
                        onClick={() => setExpanded(isOpen ? null : row.id)}
                        title={reasons.join('\n')}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 ring-1 ring-amber-200 hover:bg-amber-100 transition"
                      >
                        ⚠ Review
                      </button>
                    )}
                    <button onClick={() => setExpanded(isOpen ? null : row.id)} className="text-sm font-medium text-slate-800 hover:text-orange-700 text-left">
                      {row.measure_label}
                    </button>
                    <span className="text-xs text-slate-400">{row.entity_name}</span>
                    <div className="flex-1" />
                    {rate !== null && (
                      <span className={`text-sm font-semibold ${(row.direction === 'lower' ? rate <= Number(row.benchmark) : rate >= Number(row.benchmark)) ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {fmtMeasure(rate, unitOf(row))} <span className="text-slate-400 font-normal">vs {fmtMeasure(Number(row.benchmark), unitOf(row))}</span>
                      </span>
                    )}
                    {row.monitoring_status === 'missed' && row.number_of_deficiencies > 0 && (
                      <span className="text-xs text-rose-500">{row.number_of_deficiencies} late case{row.number_of_deficiencies === 1 ? '' : 's'}</span>
                    )}
                    <Sparkline points={trendFor(row.series_id)} benchmark={Number(row.benchmark)} />
                    {needsManual && <ManualEntry row={row} onSaved={load} />}
                    {row.monitoring_status === 'missed' && isCoaCompliance && !row.created_cap_id && onCreateCap && (
                      <button onClick={() => onCreateCap(row)}
                        className="px-2.5 py-1 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium transition">
                        Create corrective action
                      </button>
                    )}
                  </div>
                  {isOpen && (
                    <div className="mt-3 pl-1 text-sm text-slate-600">
                      {reasons.length > 0 && (
                        <div className="mb-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2">
                          <p className="text-xs font-semibold text-amber-800 mb-1">Worth a look before relying on this number:</p>
                          <ul className="text-xs text-amber-700 space-y-0.5">
                            {reasons.map((r, i) => <li key={i}>· {r}</li>)}
                          </ul>
                        </div>
                      )}
                      <p className="mb-2">{row.activity_description}</p>
                      {(row.metrics || []).filter((m) => m.denominator > 0).length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                          {(row.metrics || []).filter((m) => m.denominator > 0).map((m) => (
                            <div key={m.metric_key} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-xs">
                              <span className="text-slate-600 truncate mr-2">{m.metric_label}</span>
                              <span className={`font-semibold flex-shrink-0 ${m.threshold_met === false ? 'text-rose-600' : 'text-emerald-600'}`}>
                                {m.numerator}/{m.denominator} · {m.value}%
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {byPeriod.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-10 text-center text-slate-500 text-sm">
          Measures are configured. The first entries will appear when the current period closes
          {isCoaCompliance ? ', or click "Check for new results" to generate them now.' : '.'}
        </div>
      )}

      {wizardOpen && (
        <SetupWizard measures={measures} fdrs={fdrs} departments={departments} onClose={() => setWizardOpen(false)}
          onSaved={() => { setWizardOpen(false); refresh(); }} />
      )}
    </div>
  );
}
